import React, { useState, useEffect } from "react";
import html2canvas from "html2canvas";
import service from "../services/service";
import instructorService from "../services/instructorService";
import UserService from "../services/UserService";


const ScreenCaptureArea = () => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [image, setImage] = useState(null);
  const [description, setDescription] = useState("");
  const [uploadStatus, setUploadStatus] = useState(null);

  // 🔹 Cursor changes when selecting
  useEffect(() => {
    document.body.style.cursor = isSelecting ? "crosshair" : "default";
  }, [isSelecting]);

  useEffect(() => {
    if (!isSelecting) return;

    const handleMouseDown = (e) => {
      e.preventDefault();
      setStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e) => {
      if (!start) return;
      setEnd({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = async () => {
      if (!start || !end) return;
      setIsSelecting(false);

      const rect = {
        left: Math.min(start.x, end.x) + window.scrollX,
        top: Math.min(start.y, end.y) + window.scrollY,
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y),
      };

      const canvas = await html2canvas(document.body, {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
        useCORS: true,
        allowTaint: true,
        scale: 2,
      });

      const dataURL = canvas.toDataURL("image/png");
      setImage(dataURL);
      setStart(null);
      setEnd(null);
    };

    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
    };
  }, [isSelecting, start, end]);

  // 🆕 handle upload (with image + description)
  const handleUpload = async () => {
    if (!image || !description.trim()) {
      setUploadStatus("⚠️ Please add a description before submitting.");
      return;
    }

    setUploadStatus("Uploading...");

    let userId = UserService.getUserid();

    try {
      const blob = await (await fetch(image)).blob();
      const title = "screenshot";
      const formData = new FormData();
      formData.append("file", blob, `screenshot_${Date.now()}.png`);
      formData.append("description", description);
      formData.append("title", title);
      formData.append("userId", userId);

      instructorService.uploadScreenShotImage(formData)
        .then(res => {
          setUploadStatus(res.data == "success" ? "✅ Uploaded successfully!" : "❌ Upload failed: " + res.data);
          if (res.data == "success") {
            setDescription("");
            setImage(null);
          }
        })
        .catch(err => {

        })
    } catch (err) {
      console.error("Upload error:", err);
      setUploadStatus("❌ Error during upload");
    }
  };

  const handleRetake = () => {
    setImage(null);
    setUploadStatus(null);
    setDescription("");
    setIsSelecting(true);
  };

  return (
    <>
      {/* Capture button */}
      {!image && (
        <>
          {/* Wrapper for button + tooltip */}
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              left: "15px",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems:"flex-start",
            }}
            onMouseEnter={(e) => {
              const tooltip = e.currentTarget.querySelector(".tooltip-inline");
              if (tooltip) tooltip.style.opacity = 1;
            }}
            onMouseLeave={(e) => {
              const tooltip = e.currentTarget.querySelector(".tooltip-inline");
              if (tooltip) tooltip.style.opacity = 0;
            }}
          >
            {/* Tooltip */}
            <div
              className="tooltip-inline"
              style={{
                background: "rgba(0,0,0,0.8)",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: "6px",
                marginBottom: "8px",
                fontSize: "13px",
                textAlign: "center",
                width: "220px",
                transition: "opacity 0.3s ease",
                opacity: 0,
                pointerEvents: "none",
              }}
            >
              💡 Take a screenshot and submit to admin if you’re facing any issue.
            </div>

            {/* Screenshot button */}
            <button
              onClick={() => {
                setIsSelecting(true);
                setImage(null);
              }}
              style={{
                background: "#2b6dd8ff",
                color: "#fff",
                border: "none",
                padding: "10px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "500",
                boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
              }}
            >
              📸 Take Screenshot
            </button>
          </div>
        </>

      )}

      {/* Selection overlay */}
      {isSelecting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.05)",
            zIndex: 9998,
            cursor: "crosshair",
          }}
        >
          {start && end && (
            <div
              style={{
                position: "absolute",
                left: Math.min(start.x, end.x),
                top: Math.min(start.y, end.y),
                width: Math.abs(end.x - start.x),
                height: Math.abs(end.y - start.y),
                border: "2px dashed #2196f3",
                backgroundColor: "rgba(33,150,243,0.2)",
              }}
            />
          )}
        </div>
      )}

      {/* Preview modal with description input */}
      {image && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              padding: "20px",
              textAlign: "center",
              width: "80%",
              maxWidth: "600px",
            }}
          >
            <h3>📷 Screenshot Preview</h3>
            <img
              src={image}
              alt="Screenshot Preview"
              style={{
                maxWidth: "100%",
                border: "1px solid #ddd",
                borderRadius: "8px",
                margin: "10px 0",
              }}
            />

            {/* 🆕 Description Input */}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description about the captured issue..."
              style={{
                width: "100%",
                height: "80px",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                resize: "none",
                marginBottom: "10px",
              }}
            />

            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button
                onClick={handleUpload}
                style={{
                  background: "#4caf50",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Submit
              </button>
              <button
                onClick={handleRetake}
                style={{
                  background: "#dc3545",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Retake
              </button>
              <button
                onClick={() => setImage(null)}
                style={{
                  background: "#999",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
            {uploadStatus && (
              <p style={{ marginTop: "10px", color: "#333" }}>{uploadStatus}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ScreenCaptureArea;
